// @ts-nocheck
import { useState } from 'react'
import { Phone, ArrowRight, Loader2 } from 'lucide-react'
import { useCallStore } from '../stores/callStore'
import { useTwilioDevice } from '../hooks/useTwilioDevice'
import { formatPhoneNumberForDisplay } from '../utils/phoneNumberHelper'
import { logger } from '../utils/logger'

/**
 * 内線通話中に保留中の通話を転送するパネル
 *
 * 表示条件: internalCallStatus === 'connected' && heldCalls.length > 0 && internalCallPeerUserId != null
 * 相手オペレータIDは callStore.internalCallPeerUserId から取得（accept後も保持される）
 */
export default function InternalCallTransferPanel() {
  const internalCallStatus = useCallStore((state) => state.internalCallStatus)
  const internalCallPeerUserId = useCallStore((state) => state.internalCallPeerUserId)
  const heldCalls = useCallStore((state) => state.heldCalls)
  const callerName = useCallStore((state) => state.callerNumber) // 内線通話中は相手の名前がcallerNumberに入る

  const { transferToOperator } = useTwilioDevice()

  const [transferring, setTransferring] = useState(null) // 転送中のholdQueueName

  // 内線通話中でない、保留中の通話がない、または相手IDがない場合は表示しない
  if (internalCallStatus !== 'connected' || heldCalls.length === 0 || !internalCallPeerUserId) {
    return null
  }

  const targetOperatorName = callerName || '相手'

  // 転送ハンドラー
  const handleTransfer = async (holdQueueName, heldCallerNumber) => {
    if (!confirm(`保留中の通話（${formatPhoneNumberForDisplay(heldCallerNumber)}）を ${targetOperatorName} に転送しますか？\n\n内線通話は自動的に切断されます。`)) {
      return
    }

    setTransferring(holdQueueName)

    try {
      logger.debug('[InternalCallTransferPanel] 転送開始:', {
        holdQueueName,
        targetOperatorId: internalCallPeerUserId,
        targetOperatorName
      })

      // 内線通話を切断（状態を先にクリアしてdisconnect handlerのendCall/disconnectAllを防止）
      const currentActiveCall = useCallStore.getState().activeCall
      logger.debug('[InternalCallTransferPanel] 内線通話を切断')
      useCallStore.setState({
        activeCall: null,
        currentCallId: null,
        currentInternalCallId: null,
        internalCallPeerUserId: null,
        incomingInternalCall: null,
        internalCallStatus: 'idle',
        callStatus: 'idle',
        callerNumber: null,
      })
      if (currentActiveCall) {
        currentActiveCall.disconnect()
      }

      // 相手側のdisconnectイベントが処理される時間を確保
      await new Promise(resolve => setTimeout(resolve, 500))

      // 転送実行（バックエンドが保留中の通話を転送先オペレータにリダイレクト）
      await transferToOperator(holdQueueName, internalCallPeerUserId)

      logger.info('[InternalCallTransferPanel] 転送成功')

    } catch (error) {
      logger.error('[InternalCallTransferPanel] 転送エラー:', error)
      alert(`転送に失敗しました: ${error.message}`)
    } finally {
      setTransferring(null)
    }
  }

  return (
    <div className="fixed bottom-24 right-6 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-40 animate-slide-up">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-500 to-blue-600">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Phone className="w-4 h-4" />
          保留中の通話を転送
        </h3>
        <p className="text-xs text-blue-100 mt-1">
          {targetOperatorName} に転送できます
        </p>
      </div>

      <div className="p-4 max-h-80 overflow-y-auto">
        {heldCalls.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
            保留中の通話はありません
          </p>
        ) : (
          <div className="space-y-2">
            {heldCalls.map((heldCall) => (
              <div
                key={heldCall.holdQueueName}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
              >
                <div className="flex-1 min-w-0 mr-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                    {formatPhoneNumberForDisplay(heldCall.callerNumber)}
                  </div>
                  {heldCall.customerName && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {heldCall.customerName}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    保留者: {heldCall.operatorName}
                  </div>
                </div>

                <button
                  onClick={() => handleTransfer(heldCall.holdQueueName, heldCall.callerNumber)}
                  disabled={transferring !== null}
                  className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {transferring === heldCall.holdQueueName ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      転送中...
                    </>
                  ) : (
                    <>
                      <ArrowRight className="w-4 h-4" />
                      転送
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
        <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
          転送後、内線通話は自動的に切断されます
        </p>
      </div>
    </div>
  )
}
